import api from '@api/endpoints';
import { getErrorMessage } from '@utils';
import { REFERRALS as types } from '@store/constants';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import { v4 as uuidV4 } from 'uuid';
const CAMPAIGN_TYPE = 'PLATFORM_CAMPAIGNS';

export const fetchCampaigns =
  (reqData, refresh = false) =>
    async (dispatch) => {
      try {
        dispatch({
          type: types.FETCHING_ACTIVE_CAMPAIGNS,
          payload: {
            refresh,
            pageNo: reqData.pageNo
          }
        });

        const { data } = await api.referrals.fetchCampaigns({
          pageNo: reqData.pageNo,
          pageSize: reqData.pageSize
        });

        // API returns plain array, calculate totalPages based on result length
        // Assume if we get less than pageSize, we're on the last page
        const isLastPage = !data || data.length < reqData.pageSize;
        const totalPages = isLastPage
          ? reqData.pageNo + 1
          : reqData.pageNo + 2; // At least one more page

        return dispatch({
          type: types.FETCH_ACTIVE_CAMPAIGNS_SUCCESS,
          payload: {
            data: data || [],
            page: reqData.pageNo,
            totalPages: totalPages
          }
        });
      } catch (error) {
        return dispatch({
          type: types.FETCH_ACTIVE_CAMPAIGNS_FAILURE,
          payload: error
        });
      }
    };

export const createCampaignV2 =
  (formData, onSuccess, onError, onFinish) => async (dispatch) => {
    try {
      const uuid = uuidV4();
      const { data } = await api.rewardsV2.createCampaignByCampaignType({
        campaignType: CAMPAIGN_TYPE,
        requestBody: {
          // Note: No significance in rewards service, just a unique identifier required by BE.
          promoter_id: `ADMIN-${uuid}`,
          kwargs: {
            channel: formData.channel,
            code: formData.code,
            csv_allowed_counties: formData.eligibleCountries.join(',')
          }
        }
      });

      if (onSuccess) onSuccess(data);

      dispatch(
        setAlert({
          title: `Campaign ${formData.code} successfully created!`,
          status: ALERT_STATUS_TYPE.SUCCESS
        })
      );
    } catch (error) {
      if (onError) onError(error);

      const genericMessage =
        'Failed to create campaign. Status code: ' + error.status;

      dispatch(
        setAlert({
          title: error.message || genericMessage,
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
    } finally {
      if (onFinish) onFinish();
    }
  };

export const getRedeemedCoins = (searchReqData) => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_REDEEMED_COINS
    });

    const { data } = await api.referralsV2.getRedeemedCoins(searchReqData);

    return dispatch({
      type: types.GET_REDEEMED_COINS_SUCCESS,
      payload: data
    });
  } catch (error) {
    dispatch(
      setAlert({
        title: getErrorMessage({
          error,
          genericMessage: 'Failed to get redeemed coins!'
        }),
        status: ALERT_STATUS_TYPE.ERROR
      })
    );
    dispatch({
      type: types.GET_REDEEMED_COINS_FAILURE,
      payload: error
    });
  }
};

export const getUserRedeemedCoins =
  (userId, searchReqData) => async (dispatch) => {
    try {
      dispatch({
        type: types.GETTING_USER_REDEEMED_COINS
      });

      const { data } = await api.referralsV2.getUserRedeemedCoins(
        userId,
        searchReqData
      );

      return dispatch({
        type: types.GET_USER_REDEEMED_COINS_SUCCESS,
        payload: data
      });
    } catch (error) {
      dispatch(
        setAlert({
          title: getErrorMessage({
            error,
            genericMessage: 'Failed to get redeemed coins!'
          }),
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      dispatch({
        type: types.GET_USER_REDEEMED_COINS_FAILURE,
        payload: error
      });
    }
  };

export const redeemCoinsCompleted =
  ({ redeemId, metadata, onSuccess, onFinish }) =>
    async (dispatch) => {
      try {
        dispatch({
          type: types.COMPLETING_REDEEMED_COINS
        });
        await api.referralsV2.markRedeemCoinsCompleted({ redeemId, metadata });
        if (onSuccess) onSuccess();

        dispatch(
          setAlert({
            title: 'Updated Redeemd coins status to completed!',
            status: ALERT_STATUS_TYPE.SUCCESS
          })
        );

        return dispatch({
          type: types.COMPLETE_REDEEMED_COINS_SUCCESS
        });
      } catch (error) {
        dispatch(
          setAlert({
            title: getErrorMessage({
              error,
              genericMessage: 'Failed to update redeem coins status to completed!'
            }),
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return dispatch({
          type: types.COMPLETE_REDEEMED_COINS_FAILURE,
          payload: error
        });
      } finally {
        if (onFinish) onFinish();
      }
    };
